import PropTypes from "prop-types";
import { Link as RouterLink, useLocation } from "react-router-dom";

import {
  Box,
  Breadcrumbs,
  Container,
  Link,
  Stack,
  Typography,
} from "@mui/material";

import { NAVIGATION } from "@/navigation";

// Flatten the navigation tree into { path, title } pairs so the page title can
// be derived from the current route when a page does not pass `title`
// explicitly (mirrors Toolpad's PageContainer behaviour).
const flattenNavigation = (items, prefix = "") =>
  items.flatMap((item) => {
    // A group may omit `segment` to group items in the drawer without owning a
    // URL segment; its children still live under the current prefix.
    if (!item.segment) {
      return item.children ? flattenNavigation(item.children, prefix) : [];
    }
    const path = `${prefix}/${item.segment}`;
    const self = item.title ? [{ path, title: item.title }] : [];
    const children = item.children
      ? flattenNavigation(item.children, path)
      : [];
    return [...self, ...children];
  });

const NAVIGATION_PATHS = flattenNavigation(NAVIGATION);

const deriveTitle = (pathname) => {
  let best = null;
  for (const entry of NAVIGATION_PATHS) {
    const matches =
      pathname === entry.path || pathname.startsWith(`${entry.path}/`);
    if (matches && (!best || entry.path.length > best.path.length)) {
      best = entry;
    }
  }
  return best?.title ?? "";
};

/**
 * Drop-in replacement for @toolpad/core's PageContainer. Renders the page title
 * (explicit or derived from the route), optional breadcrumbs, and the toolbar
 * slot, then the page content inside a width-constrained container.
 *
 * Supports the props used across the app:
 *   <PageContainer maxWidth="xl" title="..." breadcrumbs={[...]}
 *     slotProps={{ header: { slotProps: { toolbar: { children } } } }} />
 */
export const PageContainer = ({
  children,
  title,
  breadcrumbs,
  maxWidth = "lg",
  slotProps,
}) => {
  const { pathname } = useLocation();
  const resolvedTitle = title ?? deriveTitle(pathname);
  const toolbar = slotProps?.header?.slotProps?.toolbar?.children ?? null;

  return (
    <Container maxWidth={maxWidth} sx={{ py: 3 }}>
      {breadcrumbs?.length ? (
        <Breadcrumbs sx={{ mb: 1 }}>
          {breadcrumbs.map((crumb, index) =>
            crumb.path ? (
              <Link
                key={crumb.path}
                component={RouterLink}
                to={crumb.path}
                underline="hover"
                color="inherit"
              >
                {crumb.title}
              </Link>
            ) : (
              <Typography key={index} sx={{
                color: "text.primary"
              }}>
                {crumb.title}
              </Typography>
            ),
          )}
        </Breadcrumbs>
      ) : null}
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 3
        }}>
        <Typography variant="h4" component="h1">
          {resolvedTitle}
        </Typography>
        {toolbar ? (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
          >
            {toolbar}
          </Box>
        ) : null}
      </Stack>
      {children}
    </Container>
  );
};

PageContainer.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({ title: PropTypes.string, path: PropTypes.string }),
  ),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  slotProps: PropTypes.object,
};
